import React from "react";
import classNames from "classnames";
import styles from "./index.module.css";

interface Props {
  containerClassName?: string; // mainly for applying margin or max-width
  className?: string;
  style?: React.CSSProperties; // usually for specific max-width, min-width
  value: any;
  placeholder?: string;
  onChange: (val: string) => void;
  onKeyEnter?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  fontSize: "md" | "lg";
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  prependClassName?: string; // mainly to adjust padding for prepend component
  appendClassName?: string; // mainly to adjust padding for append component
  type?: string;
  error?: boolean;
  variant?: "default" | "filled"; // input component style theming
}

export const BoxedInput = ({
  containerClassName,
  className,
  style,
  value,
  placeholder,
  onChange,
  onKeyEnter,
  disabled,
  autoFocus,
  id,
  fontSize,
  prepend,
  append,
  prependClassName,
  appendClassName,
  type,
  error,
  variant,
  ...rest
}: Props) => {
  const inputClass = {
    [styles.inputDisabled]: disabled,
    [styles.inputWithPrepend]: prepend,
    [styles.inputWithAppend]: append,
    [styles.inputError]: error,
    [styles.inputFilled]: variant === "filled",
  };

  const fontClass = {
    [styles.fontMedium]: fontSize === "md",
    [styles.fontLarge]: fontSize === "lg",
  };

  const handleKeyEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.code === "Enter") {
      onKeyEnter();
    }
  };

  return (
    <div
      className={classNames(
        styles.inputContainer,
        fontClass,
        containerClassName
      )}
    >
      {prepend && (
        <div className={classNames(styles.inputPrepend, prependClassName)}>
          {prepend}
        </div>
      )}
      <input
        type={type}
        className={classNames(styles.input, inputClass, className)}
        style={style}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyEnter}
        disabled={disabled}
        autoFocus={autoFocus}
        id={id}
        data-testid={id}
        {...rest}
      />
      {append && (
        <div className={classNames(styles.inputAppend, appendClassName)}>
          {append}
        </div>
      )}
    </div>
  );
};

BoxedInput.defaultProps = {
  onKeyEnter: () => {},
  fontSize: "md",
  type: "text",
  variant: "default",
};
